"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";

export default function ClosingCTA({
  eyebrow,
  titleLine1,
  titleLine2,
  paragraph,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  backgroundSrc,
  backgroundAlt,
}: {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  paragraph: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryHref: string;
  backgroundSrc: string;
  backgroundAlt: string;
}) {
  return (
    <section className="relative overflow-hidden">
      <div className="relative min-h-[600px] w-full">
        <Image
          src={backgroundSrc}
          alt={backgroundAlt}
          fill
          className="object-cover"
          sizes="100vw"
        />

        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/30 to-black/45" />

        <div className="relative z-10 mx-auto flex min-h-[600px] max-w-4xl items-center px-4 py-24 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="w-full"
          >
            <p className="font-body font-light uppercase tracking-[0.35em] text-white/60 text-xs md:text-sm">
              {eyebrow}
            </p>

            <h2 className="mt-7 font-heading text-5xl md:text-6xl leading-[1.1] font-light text-white">
              {titleLine1}
              <br />
              <span className="italic">{titleLine2}</span>
            </h2>

            <p className="mx-auto mt-8 max-w-xl font-body font-light text-base md:text-lg text-white/75 leading-relaxed">
              {paragraph}
            </p>

            <div className="mt-11 flex flex-wrap items-center justify-center gap-3">
              <Link
                href={primaryHref}
                className="inline-flex rounded-full bg-white px-7 py-3 text-xs tracking-[0.18em] uppercase text-black hover:opacity-90 transition-opacity"
              >
                {primaryLabel}
              </Link>

              <Link
                href={secondaryHref}
                className="inline-flex rounded-full border border-white/25 bg-white/5 px-7 py-3 text-xs tracking-[0.18em] uppercase text-white hover:bg-white/10 transition-colors"
              >
                {secondaryLabel}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
